import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES, VALUES, TEAM } from "../data/content";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";

export default function About() {
  return (
    <div data-testid="page-about">
      <Seo
        title="About — Helping Local Businesses Build With Greater Clarity"
        description="The Mogul Group Advisors serves entrepreneurs and businesses that need coordinated support across strategy, marketing, web design, branding, and operations."
        path="/about"
      />
      <section className="bg-white pt-20 pb-10">
        <div className="container-wide max-w-4xl">
          <div className="overline mb-6">About</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="about-page-headline">
            Helping Local Businesses Build With Greater Clarity.
          </h1>
          <p className="mt-6 text-lg text-[#4B5563] leading-relaxed">
            The Mogul Group Advisors serves entrepreneurs and businesses that need coordinated support across strategy, marketing, web design, branding, and operations — without piecing together separate agencies.
          </p>
        </div>
      </section>

      <section className="bg-[#F7F7F8]">
        <div className="container-wide py-16 md:py-24 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6 space-y-10">
            <div>
              <div className="overline mb-3">Our purpose</div>
              <p className="text-[#4B5563] leading-relaxed">
                Local businesses shape the character of every neighborhood in Dallas–Fort Worth. Our purpose is to give those owners the strategy, systems, and creative support they need to grow with confidence.
              </p>
            </div>
            <div>
              <div className="overline mb-3">Our approach</div>
              <p className="text-[#4B5563] leading-relaxed">
                We work hands-on. Every recommendation is written to be implemented — not filed away. Where useful, we implement it with you: websites, brand systems, campaigns, and operational workflows.
              </p>
            </div>
            <div>
              <div className="overline mb-3">Why local business matters</div>
              <p className="text-[#4B5563] leading-relaxed">
                When neighborhood businesses grow, so does the community around them. We are personally invested in seeing DFW's independent operators thrive.
              </p>
            </div>
            <div>
              <div className="overline mb-3">Our commitment to practical implementation</div>
              <p className="text-[#4B5563] leading-relaxed">
                A plan that cannot be executed is not a plan. Our engagements are built around clear priorities, sensible timelines, and measurable next steps.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6">
            <img src={IMAGES.heroSecondary} alt="A working session with a local business owner" className="w-full h-[600px] object-cover" />
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="bg-white" id="team">
        <div className="container-wide py-20 md:py-28">
          <div className="max-w-3xl mb-14">
            <div className="overline mb-4">Meet the Team</div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight">The people behind the work.</h2>
            <p className="mt-4 text-[#4B5563] leading-relaxed">
              A hands-on team combining operational leadership, learning &amp; development, and client-relations expertise — with clients working directly with the people doing the work.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-testid="team-grid">
            {TEAM.map((m, i) => (
              <Reveal key={m.slug} delay={i * 80}>
                <div className="group" data-testid={`team-card-${m.slug}`}>
                  <div className="relative overflow-hidden bg-[#F7F7F8]">
                    <img src={m.photo} alt={`${m.name} — ${m.title}`} className="w-full h-96 object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="mt-5">
                    <div className="flex items-center gap-2 text-sm text-gold">
                      <span>◆</span>
                      <span className="tracking-wide">{m.title}</span>
                    </div>
                    <h3 className="mt-2 font-serif text-2xl leading-tight">{m.name}</h3>
                    <p className="mt-3 text-sm text-[#4B5563] leading-relaxed">{m.bio}</p>
                    {m.link && (
                      <Link to={m.link} className="btn btn-ghost mt-4 text-sm" data-testid={`team-link-${m.slug}`}>
                        Read full biography <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </Link>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F7F7F8]">
        <div className="container-wide py-16 md:py-24">
          <div className="overline mb-4">Our values</div>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight">The standards that shape our work.</h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 50}>
                <div className="card-base h-full" data-testid={`value-card-${i}`}>
                  <div className="font-serif text-xl">{v.title}</div>
                  <p className="mt-3 text-sm text-[#4B5563] leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16">
            <Link to="/growth-assessment" className="btn btn-primary" data-testid="about-cta">Get Your Free Growth Assessment</Link>
          </div>
        </div>
      </section>

      {/* PROOF (was: TESTIMONIALS empty state) */}
      <section className="bg-white" id="proof" data-testid="proof-section">
        <div className="container-wide py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="overline mb-4">Proof of Work</div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight">Proven Through Real Client Work.</h2>
            <p className="mt-4 text-[#4B5563] leading-relaxed">
              We publish real engagements, not staged testimonials. See how we've helped Dallas businesses across hospitality, home services, restoration, wellness, and specialty retail move from stalled to growing.
            </p>
            <Link to="/portfolio" className="btn btn-primary mt-8" data-testid="proof-portfolio-cta">
              See Client Work <span className="ml-1">→</span>
            </Link>
            <p className="mt-6 text-xs text-[#6B7280] italic">Written client testimonials will be added here as clients confirm permission to publish.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
